import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosConfig';
import { BACKEND_URL } from '../../config/api';
import { 
    MdSearch, 
    MdPeople, 
    MdAccountCircle, 
    MdTrendingUp, 
    MdAccountBalanceWallet, 
    MdHourglassEmpty,
    MdTableChart,
    MdAccountTree,
    MdArrowBack,
    MdPhone
} from 'react-icons/md';

/**
 * Premium Tree Node Component
 */
const TreeNode = ({ node, isLast = false, isRoot = false, level = 0 }) => {
    const hasChildren = node.referrals && node.referrals.length > 0;
    
    // Aesthetic variants based on level
    const levelColors = [
        'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20',
        'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/20',
        'border-amber-500 bg-amber-50/50 dark:bg-amber-900/20'
    ];
    
    const levelText = [
        'text-blue-700 dark:text-blue-300',
        'text-emerald-700 dark:text-emerald-300',
        'text-amber-700 dark:text-amber-300'
    ];

    return (
        <div className="flex flex-col items-center">
            {/* The Node Card */}
            <div className={`relative flex flex-col items-center p-4 rounded-2xl border-2 transition-all duration-500 hover:scale-105 hover:shadow-xl group backdrop-blur-sm min-w-[180px] ${levelColors[level % 3]}`}>
                
                {/* Profile Image / Icon */}
                <div className="relative mb-3">
                    {node.picture ? (
                        <img 
                            src={node.picture.startsWith('http') ? node.picture : `${BACKEND_URL}${node.picture}`} 
                            alt={node.name} 
                            className="w-16 h-16 rounded-full object-cover border-4 border-white dark:border-gray-800 shadow-md group-hover:border-transparent transition-all"
                            onError={(e) => {
                                e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(node.name) + '&background=random';
                            }}
                        />
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center border-4 border-white dark:border-gray-800 shadow-md group-hover:border-transparent transition-all">
                            <MdAccountCircle className={`w-10 h-10 ${levelText[level % 3]}`} />
                        </div>
                    )}
                    
                    {/* Level Badge */}
                    <div className={`absolute -bottom-1 -right-1 px-2 py-0.5 rounded-full text-[10px] font-black shadow-sm bg-white dark:bg-gray-800 border ${levelText[level % 3]}`}>
                        {level === 0 ? 'AMBASSADOR' : `LEVEL ${level}`}
                    </div>
                </div>

                <div className="text-center">
                    <h4 className="text-sm font-black text-gray-800 dark:text-gray-100 uppercase tracking-tight line-clamp-1">{node.name}</h4>
                    <p className={`text-[10px] font-bold mt-1 opacity-80 ${levelText[level % 3]}`}>ID: {String(node.member_id).padStart(4, '0')}</p>
                    {node.mobile && (
                        <div className="flex items-center justify-center mt-2 text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                            <MdPhone className="mr-1 w-3 h-3" /> {node.mobile}
                        </div>
                    )}
                </div>

                {/* Vertical Connector Line to Children */}
                {hasChildren && (
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-8 w-[3px] h-8 bg-blue-400 dark:bg-blue-600"></div>
                )}
            </div>

            {/* Children Container */}
            {hasChildren && (
                <div className="mt-8 flex gap-12 items-start relative px-6">
                    {/* Horizontal Connector Line */}
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-blue-400 dark:bg-blue-600 mx-auto w-[calc(100%-120px)]"></div>
                    
                    {node.referrals.map((child, index) => (
                        <div key={child.id} className="relative flex flex-col items-center pt-8">
                            {/* Vertical line connecting from horizontal bar to child node */}
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[3px] h-8 bg-blue-400 dark:bg-blue-600"></div>
                            <TreeNode node={child} level={level + 1} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const MemberReferences = () => {
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'tree'
    const [ambassadors, setAmbassadors] = useState([]);
    const [selectedAmbassador, setSelectedAmbassador] = useState(null);
    const [treeData, setTreeData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch Ambassadors for the Summary Table
    const fetchAmbassadors = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get('/nai-members/members/get_ambassadors/');
            setAmbassadors(response.data);
        } catch (err) {
            console.error("Error fetching ambassadors:", err);
            setError("Failed to load ambassador data.");
        } finally {
            setLoading(false);
        }
    };

    // Fetch Specific Tree for an Ambassador
    const fetchTree = async (ambassador) => {
        setLoading(true);
        try {
            const response = await axios.get(`/nai-members/members/referral_tree/?root_id=${ambassador.member_id}`);
            setTreeData(response.data);
            setSelectedAmbassador(ambassador);
            setViewMode('tree');
        } catch (err) {
            console.error("Error fetching tree:", err);
            setError("Failed to build referral tree.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAmbassadors();
    }, []);

    const handleBack = () => {
        setViewMode('table');
        setTreeData(null);
        setSelectedAmbassador(null);
    };

    return (
        <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900/50 p-6 space-y-8 animate-in fade-in duration-500">
            
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="space-y-1">
                    <h1 className="text-3xl font-black text-gray-900 dark:text-white flex items-center tracking-tighter">
                        {viewMode === 'tree' ? (
                            <button onClick={handleBack} className="mr-4 p-2 rounded-xl bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">
                                <MdArrowBack className="text-blue-600" />
                            </button>
                        ) : (
                            <div className="p-3 rounded-2xl bg-blue-600 text-white shadow-lg shadow-blue-500/30 mr-4">
                                <MdPeople className="w-8 h-8" />
                            </div>
                        )}
                        {viewMode === 'table' ? 'ASSOCIATION AMBASSADORS' : 'AMBASSADOR NETWORK'}
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 font-medium ml-1">
                        {viewMode === 'table' 
                            ? 'Manage and monitor ambassador performance and commissions' 
                            : `Visualizing referral network for ${selectedAmbassador?.member_name}`}
                    </p>
                </div>

                {viewMode === 'table' && (
                    <div className="flex gap-4">
                         <div className="px-6 py-3 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center">
                            <MdAccountTree className="text-blue-500 w-5 h-5 mr-3" />
                            <div>
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Total Ambassadors</p>
                                <p className="text-lg font-black text-gray-800 dark:text-white uppercase">{ambassadors.length}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Error Message */}
            {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-2xl font-bold flex items-center animate-bounce">
                    <span className="mr-2">⚠️</span> {error}
                </div>
            )}

            {/* View Switching Logic */}
            <div className="relative">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 animate-pulse">
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                        <p className="text-xl font-bold text-gray-400 uppercase tracking-widest">Synergizing Data...</p>
                    </div>
                ) : viewMode === 'table' ? (
                    /* SUMMARY TABLE VIEW */
                    <div className="bg-white dark:bg-gray-800 rounded-[2.5rem] shadow-2xl shadow-blue-500/5 border border-white dark:border-gray-700 overflow-hidden animate-in slide-in-from-bottom duration-700">
                        <div className="p-8 border-b border-gray-50 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                            <h2 className="text-xl font-black text-gray-800 dark:text-white uppercase tracking-tight flex items-center">
                                <MdTableChart className="mr-3 text-blue-500" /> Performance Leaderboard
                            </h2>
                            {/* Search bar could go here */}
                        </div>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50/50 dark:bg-gray-900/20">
                                        <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">ID / Ambassador</th>
                                        <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest">Network Size</th>
                                        <th className="px-8 py-5 text-xs font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">Total Received</th>
                                        <th className="px-8 py-5 text-xs font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">Amount Pending</th>
                                        <th className="px-8 py-5 text-xs font-black text-gray-400 uppercase tracking-widest text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {ambassadors.map((amb) => (
                                        <tr key={amb.id} className="hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-colors group">
                                            <td className="px-8 py-6">
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 font-black mr-4 group-hover:scale-110 transition-transform">
                                                        {amb.picture ? (
                                                            <img src={amb.picture} className="w-full h-full rounded-full object-cover" />
                                                        ) : amb.member_name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-black text-gray-800 dark:text-gray-100 uppercase text-sm">{amb.member_name}</h4>
                                                        <p className="text-[10px] font-bold text-gray-400 tracking-tighter uppercase mt-0.5">ID: {String(amb.member_id).padStart(4, '0')} • {amb.mobile}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center">
                                                    <div className="flex flex-col">
                                                        <p className="text-lg font-black text-gray-700 dark:text-gray-200">{amb.referral_count}</p>
                                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">Members Referred</p>
                                                    </div>
                                                    <MdTrendingUp className="ml-3 text-emerald-500" />
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center space-x-3 text-emerald-600 dark:text-emerald-400">
                                                    <MdAccountBalanceWallet className="w-5 h-5 opacity-60" />
                                                    <span className="text-xl font-black">₹{amb.commission_received.toLocaleString()}</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="flex items-center space-x-3 text-amber-600 dark:text-amber-400">
                                                    <MdHourglassEmpty className="w-5 h-5 opacity-60" />
                                                    <span className="text-xl font-black">₹{amb.commission_pending.toLocaleString()}</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-center">
                                                <button 
                                                    onClick={() => fetchTree(amb)}
                                                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-0.5"
                                                >
                                                    View Network
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {ambassadors.length === 0 && (
                                        <tr>
                                            <td colSpan="5" className="py-20 text-center text-gray-400 font-black uppercase tracking-widest opacity-50">
                                                No Ambassadors Defined Yet
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    /* TREE VISUALIZATION VIEW */
                    <div className="bg-white dark:bg-gray-800 rounded-[2.5rem] shadow-2xl border border-white dark:border-gray-700 p-12 min-h-[600px] overflow-auto animate-in zoom-in-95 duration-500">
                        {treeData ? (
                            <div className="flex justify-center py-10 min-w-max">
                                <TreeNode node={treeData} isRoot={true} />
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <p className="text-gray-400 font-black uppercase text-xl animate-pulse">Mapping Network Architecture...</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MemberReferences;
